<template>
    <div
        class="group my-4 w-full rounded-lg border border-iron-200 bg-white px-4 py-5 shadow-sm transition-all duration-200 hover:shadow-md"
    >
        <div class="items-center justify-between space-x-3 sm:flex">
            <div class="flex items-center gap-2">
                <div class="flex-shrink-0"></div>
                <div class="flex min-w-0 gap-3">
                    <p class="font-medium text-iron-900">
                        {{ domain.name }}
                    </p>
                    <span
                        class="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-iron-900 ring-1 ring-inset ring-iron-200"
                    >
                        <svg
                            class="h-1.5 w-1.5"
                            :class="{
                                'text-green-500': domain.verified,
                                'text-error-500': !domain.verified,
                            }"
                            viewBox="0 0 6 6"
                            aria-hidden="true"
                        >
                            <circle fill="currentColor" cx="3" cy="3" r="3" />
                        </svg>
                        {{ $t("domains.verified") }}
                    </span>
                    <span
                        class="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-iron-900 ring-1 ring-inset ring-iron-200"
                    >
                        <svg
                            class="h-1.5 w-1.5"
                            :class="{
                                'text-green-500': domain.dkim,
                                'text-error-500': !domain.dkim,
                            }"
                            viewBox="0 0 6 6"
                            aria-hidden="true"
                        >
                            <circle fill="currentColor" cx="3" cy="3" r="3" />
                        </svg>
                        DKIM
                    </span>
                    <span
                        class="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-iron-900 ring-1 ring-inset ring-iron-200"
                    >
                        <svg
                            class="h-1.5 w-1.5"
                            :class="{
                                'text-green-500': domain.cname,
                                'text-error-500': !domain.cname,
                            }"
                            viewBox="0 0 6 6"
                            aria-hidden="true"
                        >
                            <circle fill="currentColor" cx="3" cy="3" r="3" />
                        </svg>
                        CNAME
                    </span>
                </div>
            </div>

            <div
                class="mt-3 flex flex-shrink-0 justify-start self-center sm:mt-0"
            >
                <div class="inline-flex rounded-md shadow-sm">
                    <button
                        :disabled="loading"
                        class="relative ml-2 inline-flex items-center gap-1.5 rounded-l-md rounded-r-md bg-white px-3 py-2 text-sm font-medium text-iron-900 ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                        @click="verifyDomain"
                    >
                        <RefreshCw :stroke-width="1.6" :size="16" />
                        <span class="hidden sm:inline-block">{{
                            $t("domains.verify")
                        }}</span>
                    </button>

                    <ShowRecordsModal :domain="domain">
                        <button
                            class="relative ml-2 inline-flex items-center gap-1 rounded-l-md rounded-r-md bg-white px-3 py-2 text-sm font-medium text-iron-900 ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                        >
                            <span class="hidden sm:inline-block">{{
                                $t("domains.records")
                            }}</span>
                        </button>
                    </ShowRecordsModal>

                    <button
                        :disabled="loading"
                        class="relative ml-2 inline-flex items-center gap-1 rounded-l-md rounded-r-md bg-white px-3 py-2 text-sm font-medium text-iron-900 ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                        @click="deleteDomain"
                    >
                        <Trash2 :stroke-width="1.6" :size="20" />
                        <span class="hidden sm:inline-block">{{
                            $t("remove")
                        }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import useConfirm from "../../ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import { RefreshCw, Trash2 } from "lucide-vue-next";
import { ref } from "vue";
import ShowRecordsModal from "./ShowRecordsModal.vue";
const { show } = useConfirm();
const { t } = useI18n();

const props = defineProps({
    domain: {
        required: true,
        type: Object,
    },
});

const loading = ref(false);

const deleteDomain = async function () {
    const result = await show({
        header: t("domains.are_you_sure"),
        message: t("domains.delete"),
        confirm: t("domains.remove"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("app.domains.delete", {
            domain: props.domain.id,
        }),
    );
};

const verifyDomain = async function () {
    loading.value = true;

    router.post(
        route("app.domains.verify", {
            domain: props.domain.id,
        }),
        {},
        {
            preserveState: false,
            onFinish: () => {
                loading.value = false;
            },
        },
    );
};
</script>
